import { useCallback, useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Drawer,
  Divider,
  IconButton,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import LinkIcon from "@mui/icons-material/Link";
import RefreshIcon from "@mui/icons-material/Refresh";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import CableIcon from "@mui/icons-material/Cable";
import CloseIcon from "@mui/icons-material/Close";
import DeleteIcon from "@mui/icons-material/Delete";
import { useAuthApi } from "../hooks/useAuthApi";
import { OCPP, MGMT } from "../env";
import { fmtDate, statusColor } from "../format";
import type {
  Charger,
  ChargerConfig,
  ChargerOnboardingQueueItem,
  CountedPage,
  Tenant,
} from "../types";
import { useToast } from "../toast";
import OnboardChargerDialog from "../dialogs/OnboardChargerDialog";
import ConfirmDialog from "../dialogs/ConfirmDialog";

function wsBase(httpBase: string): string {
  return httpBase.replace(/^https/, "wss").replace(/^http/, "ws");
}

const WS_BASE = `${wsBase(OCPP)}/ws`;

function CopyField({ label, value }: { label: string; value: string }) {
  const [copied, setCopied] = useState(false);
  const copy = () => {
    navigator.clipboard.writeText(value).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    });
  };
  return (
    <TextField
      label={label}
      value={value}
      size="small"
      fullWidth
      slotProps={{
        input: {
          readOnly: true,
          sx: { fontFamily: "monospace", fontSize: "0.82rem" },
          endAdornment: (
            <Tooltip title={copied ? "Copied!" : "Copy"}>
              <IconButton size="small" onClick={copy} edge="end">
                <ContentCopyIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          ),
        },
      }}
    />
  );
}

function ConnectionInfoDrawer({ open, onClose, ocppToken }: { open: boolean; onClose: () => void; ocppToken: string | null }) {
  const normalizedToken = ocppToken?.trim() || null;
  const wsUrl = normalizedToken ? `${WS_BASE}/${normalizedToken}/{charge_point_id}` : `${WS_BASE}/{charge_point_id}`;
  return (
    <Drawer anchor="right" open={open} onClose={onClose}>
      <Box sx={{ width: 420, p: 3 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
          <Typography variant="h6" fontWeight={600}>
            Connection Endpoints
          </Typography>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Stack>
        <Divider sx={{ mb: 3 }} />

        <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
          WebSocket (OCPP)
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
          Configure this URL on the charger. Replace <code>{"{charge_point_id}"}</code> with the
          charger's unique identifier.
        </Typography>
        {normalizedToken ? (
          <CopyField label="Key UUID" value={normalizedToken} />
        ) : (
          <Alert severity="warning" sx={{ mb: 1.5 }}>
            Could not load onboarding key UUID.
          </Alert>
        )}
        {normalizedToken && <Box sx={{ mb: 1.5 }} />}
        <CopyField
          label="WebSocket URL"
          value={wsUrl}
        />

        <Divider sx={{ my: 3 }} />

        <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
          REST API
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
          Base URL for the OCPP management API.
        </Typography>
        <CopyField label="REST base URL" value={OCPP} />

        <Divider sx={{ my: 3 }} />

        <Typography variant="body2" color="text.secondary">
          Once the charger connects using the WebSocket URL it will appear in the onboarding queue
          below, ready to be linked to a location.
        </Typography>
      </Box>
    </Drawer>
  );
}

export default function Onboarding() {
  const api = useAuthApi();
  const [items, setItems] = useState<Charger[]>([]);
  const [configs, setConfigs] = useState<ChargerConfig[]>([]);
  const [onboardingQueue, setOnboardingQueue] = useState<ChargerOnboardingQueueItem[]>([]);
  const [tenant, setTenant] = useState<Tenant | null>(null);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState<Charger | null>(null);
  const [toDelete, setToDelete] = useState<Charger | null>(null);
  const [infoOpen, setInfoOpen] = useState(false);
  const toast = useToast();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [chargers, cfgs, t, queue] = await Promise.all([
        api<Charger[]>(`${OCPP}/chargers`),
        api<CountedPage<ChargerConfig>>(`${MGMT}/charger-configs`),
        api<Tenant>(`${MGMT}/tenants/me`),
        api<ChargerOnboardingQueueItem[]>(`${OCPP}/chargers/onboarding-queue`),
      ]);
      const allChargers = chargers ?? [];
      const allConfigs = cfgs?.items ?? [];
      setConfigs(allConfigs);
      setTenant(t);
      const assignedIds = new Set(
        allConfigs.filter((c) => c.location_id != null).map((c) => c.charge_point_id)
      );
      setOnboardingQueue(
        (queue ?? []).filter((entry) => !assignedIds.has(entry.charge_point_id))
      );
      setItems(allChargers.filter((c) => !assignedIds.has(c.charge_point_id)));
    } catch (e) {
      toast.show((e as Error).message, "error");
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    load();
  }, [load]);

  const configFor = (charger: Charger) =>
    configs.find((c) => c.charge_point_id === charger.charge_point_id) ?? null;

  const deleteCharger = async () => {
    if (!toDelete) return;
    try {
      await api(`${OCPP}/chargers/${toDelete.id}`, { method: "DELETE" });
      toast.show("Charger deleted");
      setToDelete(null);
      load();
    } catch (e) {
      toast.show((e as Error).message, "error");
    }
  };

  const dismissQueueEntry = async (entryId: number) => {
    try {
      await api(`${OCPP}/chargers/onboarding-queue/${entryId}`, { method: "DELETE" });
      setOnboardingQueue((current) => current.filter((entry) => entry.id !== entryId));
      toast.show("Queue entry dismissed");
    } catch (e) {
      toast.show((e as Error).message, "error");
    }
  };

  return (
    <>
      <Box sx={{ mb: 2, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <Typography variant="body2" color="text.secondary">
          Chargers that have connected but are not yet assigned to a location.
        </Typography>
        <Stack direction="row" spacing={1}>
          <Button
            variant="outlined"
            startIcon={<CableIcon />}
            onClick={() => setInfoOpen(true)}
          >
            Connection Info
          </Button>
          <Button startIcon={<RefreshIcon />} onClick={load} disabled={loading}>
            Refresh
          </Button>
        </Stack>
      </Box>

      {onboardingQueue.length > 0 && (
        <Stack spacing={1} sx={{ mb: 2 }}>
          {onboardingQueue.map((entry) => (
            <Alert
              key={entry.id}
              severity={entry.queue_status === "duplicate_charge_point_id" ? "warning" : "info"}
              action={
                <IconButton
                  aria-label="dismiss queue entry"
                  color="inherit"
                  size="small"
                  onClick={() => void dismissQueueEntry(entry.id)}
                >
                  <CloseIcon fontSize="inherit" />
                </IconButton>
              }
            >
              Queue #{entry.id}:{" "}
              <Box component="span" sx={{ fontFamily: "monospace" }}>
                {entry.charge_point_id}
              </Box>
              {" "}({entry.queue_status.replaceAll("_", " ")}, {entry.occurrences} attempts, last seen {fmtDate(entry.last_seen_at)}).
            </Alert>
          ))}
        </Stack>
      )}

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Charge Point ID</TableCell>
              <TableCell>Vendor / Model</TableCell>
              <TableCell>OCPP</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Connection</TableCell>
              <TableCell>First Seen</TableCell>
              <TableCell>Last Heartbeat</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading && (
              <TableRow>
                <TableCell colSpan={8} align="center">
                  <CircularProgress size={24} />
                </TableCell>
              </TableRow>
            )}
            {!loading && items.length === 0 && (
              <TableRow>
                <TableCell colSpan={8} align="center">
                  <Box sx={{ py: 3 }}>
                    <Typography color="text.secondary">
                      No chargers awaiting onboarding.
                    </Typography>
                  </Box>
                </TableCell>
              </TableRow>
            )}
            {items.map((c) => (
              <TableRow key={c.id} hover>
                <TableCell sx={{ fontFamily: "monospace" }}>
                  {c.charge_point_id}
                </TableCell>
                <TableCell>
                  {[c.vendor, c.model].filter(Boolean).join(" / ") || "–"}
                </TableCell>
                <TableCell>{c.ocpp_version ?? "–"}</TableCell>
                <TableCell>
                  <Chip label={c.status} color={statusColor(c.status)} size="small" />
                </TableCell>
                <TableCell>
                  <Chip
                    label={c.is_connected ? "Connected" : "Offline"}
                    color={c.is_connected ? "success" : "default"}
                    size="small"
                  />
                </TableCell>
                <TableCell>{fmtDate(c.created_at)}</TableCell>
                <TableCell>{fmtDate(c.last_heartbeat)}</TableCell>
                <TableCell align="right">
                  <Stack direction="row" spacing={1} justifyContent="flex-end">
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={<LinkIcon />}
                      onClick={() => setSelected(c)}
                    >
                      Link to Location
                    </Button>
                    {!c.is_connected && (
                      <Tooltip title="Delete offline charger">
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => setToDelete(c)}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                  </Stack>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <ConnectionInfoDrawer open={infoOpen} onClose={() => setInfoOpen(false)} ocppToken={tenant?.ocpp_token ?? null} />

      <ConfirmDialog
        open={toDelete !== null}
        title="Delete Charger"
        message={`Delete charger "${toDelete?.charge_point_id}"? This cannot be undone.`}
        onCancel={() => setToDelete(null)}
        onConfirm={deleteCharger}
      />

      {selected && (
        <OnboardChargerDialog
          charger={selected}
          existingConfig={configFor(selected)}
          onClose={() => setSelected(null)}
          onSaved={() => {
            setSelected(null);
            load();
          }}
        />
      )}
    </>
  );
}
